import { z } from 'zod';

// ---------------------------------------------------------------------------
// Consult-thread management — /api/conversations/:id/consult-threads
//
// Consultant-agents H1.2 (consult threads). PR 2 already STORES `consult_thread`
// rows (created on the first tag, single implicit thread) and tags every consult
// message row with `consult_thread_id`. This surface SURFACES those rows and lets
// the support team MANAGE them: create a named thread, list a conversation's
// threads, rename a thread, and switch a thread's engaged consultant (re-tag).
//
// ISOLATION (the PR-3 lesson): consult threads are TEAM-ONLY. Thread titles,
// existence, and engaged-consultant identity are internal disclosure — every route
// below is gated on `assertHubSupportAccess` + conversation↔hub ownership, exactly
// like the copilot-suggest endpoint. This data NEVER rides a customer/end-user
// payload (session snapshot, chat conversation list, bootstrap, live-update frame);
// it is reachable only through these support-gated reads.
// ---------------------------------------------------------------------------

/**
 * Consult-thread status vocabulary (mirrors the `consult_thread.status` CHECK-free
 * column default of `'open'`). `awaiting` and `cancelled_at_close` are reserved for
 * later horizons (H1.5/H1.7 async completion + close-path); H1.2 writes only `open`.
 */
export const CONSULT_THREAD_STATUSES = ['open', 'awaiting', 'resolved', 'cancelled_at_close'] as const;
export const consultThreadStatus = z.enum(CONSULT_THREAD_STATUSES);
export type ConsultThreadStatus = z.infer<typeof consultThreadStatus>;

/** Thread title bound — a short human label the support team gives a named thread. */
const CONSULT_THREAD_TITLE_MAX = 120;
export const consultThreadTitle = z
  .string()
  .trim()
  .min(1)
  .max(CONSULT_THREAD_TITLE_MAX);

/**
 * A consult thread as surfaced to the support view. `engaged_agent_name` is resolved
 * server-side from the hub config (the DO holds the cached roster) so the client
 * renders the currently-engaged consultant without a second lookup. It is nullable
 * because a thread can be created before any consultant is engaged, or point at a
 * since-removed agent id.
 */
export const consultThreadSummary = z.object({
  consult_thread_id: z.string(),
  conversation_id: z.string(),
  initiator_type: z.enum(['team', 'agent']),
  initiator_id: z.string(),
  engaged_agent_id: z.string().nullable(),
  engaged_agent_name: z.string().nullable(),
  title: z.string().nullable(),
  status: consultThreadStatus,
  created_at: z.string(),
});
export type ConsultThreadSummary = z.infer<typeof consultThreadSummary>;

// --- GET /:id/consult-threads --------------------------------------------------
export const listConsultThreadsQuery = z.object({
  hub_id: z.string().uuid(),
});

export const listConsultThreadsResponse = z.object({
  threads: z.array(consultThreadSummary),
});
export type ListConsultThreadsResponse = z.infer<typeof listConsultThreadsResponse>;

// --- POST /:id/consult-threads -------------------------------------------------
export const createConsultThreadBody = z.object({
  hub_id: z.string().uuid(),
  title: consultThreadTitle.optional(),
  // Optionally engage a consultant on creation. Validated server-side against the
  // hub's `consultant`-role agents; a non-consultant / unknown id is rejected.
  engaged_agent_id: z.string().uuid().optional(),
});

export const createConsultThreadResponse = z.object({
  thread: consultThreadSummary,
});
export type CreateConsultThreadResponse = z.infer<typeof createConsultThreadResponse>;

// --- PATCH /:id/consult-threads/:threadId --------------------------------------
export const consultThreadIdParam = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
});

/**
 * Rename a thread (`title`) and/or switch its engaged consultant (`engaged_agent_id`
 * — the re-tag). At least one field must be present. Switching re-points the sticky
 * engagement: the next consult send into this thread dispatches to the new
 * consultant, whose turn reads this thread's full history + the customer transcript
 * (`buildConsultContextMessages` keys on `consult_thread_id`, so sibling threads stay
 * isolated by construction).
 */
export const updateConsultThreadBody = z
  .object({
    hub_id: z.string().uuid(),
    title: consultThreadTitle.optional(),
    engaged_agent_id: z.string().uuid().optional(),
  })
  .refine((v) => v.title !== undefined || v.engaged_agent_id !== undefined, {
    message: 'Provide a title and/or an engaged_agent_id to update',
  });

export const updateConsultThreadResponse = z.object({
  thread: consultThreadSummary,
});
export type UpdateConsultThreadResponse = z.infer<typeof updateConsultThreadResponse>;

// ---------------------------------------------------------------------------
// Agent-initiated consults (H1.7b) — guardrail budgets + fixed system vocabulary
//
// Roadmap decision 9 makes these NON-NEGOTIABLE, so they live in core beside the
// thread contract rather than as backend-local magic numbers: an agent can now
// autonomously multiply consult turns, and every consult turn bills a foreground
// operation.
// ---------------------------------------------------------------------------

/**
 * Maximum consult chain depth. A pilot consulting a consultant is depth 1; that
 * consultant (with the explicit chain opt-in) consulting another is depth 2. The
 * budget is carried in the THREAD LINKAGE — a locally-created thread inherits from
 * its conversation's `delegation_callback` row, so it survives a cross-hub hop.
 */
export const CONSULT_MAX_DEPTH = 3;

/**
 * Consults one LLM COMPLETION may start.
 *
 * Named for what it actually bounds. The counter lives on the per-completion tool
 * context, so a continuation that re-enters the turn — a reinvoke, a 429 retry
 * re-dispatch, a harness drain resume, or this feature's own consult re-invocation —
 * starts a fresh allowance. Calling it "per turn" would be a claim the enforcement
 * does not back, and this series has twice shipped bugs from continuations re-entering
 * a path that assumed one pass.
 *
 * The DURABLE bound is `CONSULT_MAX_PER_CONVERSATION` below, which no continuation
 * resets. This constant is the per-call brake that stops one tool loop from firing a
 * burst; the conversation cap is what actually bounds spend.
 */
export const CONSULT_MAX_PER_COMPLETION = 2;

/**
 * Agent-initiated consult threads one conversation may accumulate over its whole
 * life. Follow-ups into an EXISTING thread are capped by this too (decision 9: the
 * budget caps follow-ups per thread, not just thread creation).
 */
export const CONSULT_MAX_PER_CONVERSATION = 12;

/**
 * Sync turn-time budget. A same-hub consultant is consulted synchronously inside the
 * initiator's tool loop; when its turn exceeds this, the runtime CONVERTS the call to
 * async — the tool result becomes "thread started, answer will follow" and delivery
 * continues on the async contract. Chosen well under the Workers request budget so a
 * slow consultant can never take the initiator's turn down with it.
 */
export const CONSULT_SYNC_TURN_BUDGET_MS = 45_000;

/**
 * How long an `awaiting` thread may stay pending before its timeout fires. "Never
 * leave a thread pending forever" — the timeout is a ConversationDO conversation
 * schedule, cancelled with the rest at close.
 */
export const CONSULT_AWAIT_TIMEOUT_MS = 15 * 60_000;

/**
 * FIXED, system-generated vocabulary for consult status rows.
 *
 * The initiator allow-set admits system rows ONLY by these codes, and renders the
 * TABLE's text — never the stored `message_text`, never a thread title, note, or
 * actor display name. That is what keeps team-authored free text out of a
 * customer-voiced turn's context even when a status row happens to sit in the thread.
 */
export const CONSULT_SYSTEM_EVENTS = {
  consult_requested: 'A consult was requested on this thread.',
  consult_timeout: 'The consult did not produce an answer in time.',
  consult_converted_async: 'The consult is taking longer than expected and will answer asynchronously.',
  consult_team_takeover: 'The support team has taken over this consult thread.',
  consult_result_for_team: 'A consult result arrived for the support team to handle.',
} as const;

export type ConsultSystemEvent = keyof typeof CONSULT_SYSTEM_EVENTS;

export function isConsultSystemEvent(value: unknown): value is ConsultSystemEvent {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CONSULT_SYSTEM_EVENTS, value);
}

/**
 * The FIXED tool result a sync consult returns once the thread is team-owned.
 *
 * Checked at BOTH ends of the call — entry and result-return — because a flip can
 * land mid-execution (team posts interleave through the target turn's awaits). A sync
 * tool result is unfiltered target output with no allow-set backstop, so a
 * mid-flight flip DISCARDS the in-flight reply and substitutes this string.
 */
export const CONSULT_TEAM_TAKEOVER_NOTICE =
  'This consult thread has been taken over by the support team. No answer is available to you here; the team is handling it directly.';

/**
 * The tool error a sync consult returns when its consultant's turn failed, and what a keyed
 * `start_consult_thread` call's re-run returns when its first run asked and no reply came (the reset
 * cut the consultant's turn too): the turn is not run again (#5083 owner decision 96).
 */
export const CONSULT_TURN_FAILED_NOTICE = 'The consultant could not be reached. Continue without their input.';
